import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import type {
  OrgModelConfigFilter,
  OrgModelConfigSummary,
  SearchOrgModelConfigsResponse,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

export interface SearchOrgModelConfigsResult {
  data: OrgModelConfigSummary[] | undefined;
  totalCount: number | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
}

/**
 * Page-aware Roboto-admin search over every org's model configuration,
 * backed by `v1/orgs/llm-providers/search`. Pages are chained by
 * `next_token` (sent back as `after`) so each is fetched at most once; the
 * hook exposes the `currentPage` slice and prefetches the next page.
 */
export function useSearchOrgModelConfigs(
  filter: OrgModelConfigFilter,
  currentPage: number,
  pageSize: number,
): SearchOrgModelConfigsResult {
  const { orgService } = useDomainServices();

  const q = useInfiniteQuery({
    queryKey: orgQueryKeys.modelConfigSearch(filter, pageSize),
    queryFn: ({ pageParam, signal }) =>
      orgService.searchOrgModelConfigs(
        { ...filter, limit: pageSize, after: pageParam },
        { abortSignal: signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: SearchOrgModelConfigsResponse) =>
      last.next_token ?? undefined,
    placeholderData: keepPreviousData,
  });

  React.useEffect(
    function prefetchNextPage() {
      if (!q.data || q.isFetchingNextPage || q.hasNextPage === false) {
        return;
      }
      if (currentPage >= q.data.pages.length - 1) {
        void q.fetchNextPage();
      }
    },
    [currentPage, q.data, q.hasNextPage, q.isFetchingNextPage, q],
  );

  const loaded = q.data?.pages.length;
  const hasNextPage =
    (loaded !== undefined && currentPage + 1 < loaded) || q.hasNextPage;

  return {
    data: q.data?.pages[currentPage]?.items,
    totalCount: q.data?.pages[0]?.total_count,
    error: q.error,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    hasNextPage,
  };
}
